import dayjs from 'dayjs';
import { connect, ConnectedProps } from 'react-redux';

import { RootState } from 'src/redux/store';
import { selectors as individualDashboardSelectors } from 'src/routes/IndividualDashboard/modules/dashboard/slice';
import {
  displayInTimezone,
  FORMAT_WITHOUT_SECONDS,
  getFormatDateByContinent,
  MONTH_DAY_YEAR_FORMAT,
  parseUtcOffsetToHours,
} from 'src/utils/timeUtils';
import { ProcessedSleepData } from 'src/routes/IndividualDashboard/modules/dashboard/types';
import { UUID } from 'src/types/utility';
import { isNullOrUndefined } from 'src/utils/parser';
import { CUSTOMER_FEATURE_SETTINGS } from 'src/routes/Customers/modules/types';
import { selectors as loggedInUserSelectors } from 'src/redux/data/loggedInUser';

export const AHI_SCORE_THRESHOLD = 15;

export enum APNEA_SCORE {
  ABOVE = 'ABOVE',
  BELOW = 'BELOW',
  NO_CLEAR_TREND = 'No clear trend',
}

export enum FAILURE_REASON {
  NOT_ENOUGH_SLEEP = 'NOT_ENOUGH_SLEEP',
  NO_DATA = 'NO_DATA',
}

type OwnProps = {
  patientIdProp: UUID;
};

const computeTrend = (
  data: (ProcessedSleepData | undefined)[],
): APNEA_SCORE | 'N/A' => {
  if (!data[0]?.ahi || !data[1]?.ahi || !data[2]?.ahi) {
    return 'N/A';
  }

  if (
    data[0]?.ahi > AHI_SCORE_THRESHOLD &&
    data[1]?.ahi > AHI_SCORE_THRESHOLD &&
    data[2]?.ahi > AHI_SCORE_THRESHOLD
  ) {
    return APNEA_SCORE.ABOVE;
  }

  if (
    data[0]?.ahi < AHI_SCORE_THRESHOLD &&
    data[1]?.ahi < AHI_SCORE_THRESHOLD &&
    data[2]?.ahi < AHI_SCORE_THRESHOLD
  ) {
    return APNEA_SCORE.BELOW;
  }

  return APNEA_SCORE.NO_CLEAR_TREND;
};

const getCurrentApneaScore = (ahi?: number | null | undefined) => {
  if (!ahi) {
    return null;
  }

  if (ahi > AHI_SCORE_THRESHOLD) {
    return APNEA_SCORE.ABOVE;
  }

  return APNEA_SCORE.BELOW;
};

const mapStateToProps = (state: RootState, { patientIdProp }: OwnProps) => {
  const sleepData =
    individualDashboardSelectors.selectSleepData(state, patientIdProp) ?? {};

  const sleepDataDays = Object.keys(sleepData);
  const lastIndex = sleepDataDays.length - 1;
  const firstIndex = sleepDataDays[0];
  const sleepLastDay = sleepDataDays[lastIndex] as string;

  if (
    !sleepLastDay ||
    !sleepData ||
    !sleepData[sleepLastDay] ||
    !Object.keys(sleepData).length
  ) {
    return {
      isLoading: individualDashboardSelectors.selectIsSleepDataLoading(
        state,
        patientIdProp,
      ),
    };
  }
  const sleepDataLastDay = sleepData[sleepLastDay];
  const sleepDataFirstDay = sleepData[firstIndex as string];

  const startOfSleepData = sleepDataFirstDay?.startTime;
  const endOfSleepData = sleepDataLastDay?.startTime;
  const sleepWakeSettings =
    individualDashboardSelectors.selectSiteFeatureSettings(
      state,
      CUSTOMER_FEATURE_SETTINGS.SLEEP_WAKE,
    );
  const timezone = loggedInUserSelectors.getUserTenantTimezone(state);
  const timeZoneOffset = parseInt(parseUtcOffsetToHours(timezone));
  return {
    patient: individualDashboardSelectors.getPatientDetails(state),
    apneaScore: getCurrentApneaScore(sleepDataLastDay?.ahi),
    startOfNight: displayInTimezone(
      dayjs(sleepDataLastDay?.startTime)
        .utc()
        .add(
          (sleepWakeSettings?.startHour as number) + timeZoneOffset ?? 12,
          'hour',
        ),
      timezone,
      FORMAT_WITHOUT_SECONDS,
    ),
    endOfNight:
      (sleepWakeSettings?.startHour ?? 12) > (sleepWakeSettings?.endHour ?? 7)
        ? displayInTimezone(
            dayjs(sleepDataLastDay?.startTime)
              .utc()
              .add(1, 'day')
              .add(
                (sleepWakeSettings?.endHour as number) + timeZoneOffset ?? 7,
                'hour',
              ),
            timezone,
            FORMAT_WITHOUT_SECONDS,
          )
        : displayInTimezone(
            dayjs(sleepDataLastDay?.startTime)
              .utc()
              .add(
                (sleepWakeSettings?.endHour as number) + timeZoneOffset ?? 7,
                'hour',
              ),
            timezone,
            FORMAT_WITHOUT_SECONDS,
          ),
    trend: computeTrend([
      sleepData[sleepDataDays[sleepDataDays.length - 1] as string],
      sleepData[sleepDataDays[sleepDataDays.length - 2] as string],
      sleepData[sleepDataDays[sleepDataDays.length - 3] as string],
    ]),
    failureReason: sleepDataLastDay?.failureReason as
      | FAILURE_REASON.NOT_ENOUGH_SLEEP
      | FAILURE_REASON.NO_DATA
      | null,
    sleepData: Object.values(sleepData).map(data => ({
      sleep: data.sleep,
      remSleep: data.remSleep,
      noSleep: isNullOrUndefined(data.sleep),
      noRemSleep: isNullOrUndefined(data.remSleep),
      highAhi:
        !isNullOrUndefined(data.ahi) && Number(data.ahi) > AHI_SCORE_THRESHOLD,
      lowAhi:
        !isNullOrUndefined(data.ahi) && Number(data.ahi) <= AHI_SCORE_THRESHOLD,
      noAhi: !!isNullOrUndefined(data.ahi),
      date: dayjs(data.startTime).format(
        getFormatDateByContinent(MONTH_DAY_YEAR_FORMAT),
      ),
    })),

    sleepDataTimeFrame: `${displayInTimezone(
      dayjs(startOfSleepData)
        .utc()
        .add(
          (sleepWakeSettings?.startHour as number) + timeZoneOffset ?? 7,
          'hour',
        ),
      timezone,
      FORMAT_WITHOUT_SECONDS,
    )} - ${
      (sleepWakeSettings?.startHour ?? 12) > (sleepWakeSettings?.endHour ?? 7)
        ? displayInTimezone(
            dayjs(endOfSleepData)
              .utc()
              .add(1, 'day')
              .add(
                (sleepWakeSettings?.endHour as number) + timeZoneOffset ?? 7,
                'hour',
              ),
            timezone,
            FORMAT_WITHOUT_SECONDS,
          )
        : displayInTimezone(
            dayjs(endOfSleepData)
              .utc()
              .add(
                (sleepWakeSettings?.endHour as number) + timeZoneOffset ?? 7,
                'hour',
              ),
            timezone,
            FORMAT_WITHOUT_SECONDS,
          )
    }`,
    isLoading: individualDashboardSelectors.selectIsSleepDataLoading(
      state,
      patientIdProp,
    ),
  };
};

const mapActionCreators = {};

const Connector = connect(mapStateToProps, mapActionCreators);

export type PropsFromRedux = ConnectedProps<typeof Connector>;
export default Connector;
